import { useEffect, useRef, useState } from 'react';
import { Loader2, Search, UserRound, X } from 'lucide-react';
import { Input } from '../ui/input';

// Admin search box on the Reports page: type a name or email, pick a learner
export const UserSearch = ({ api, onSelect }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const reqId = useRef(0);
    const inputRef = useRef(null);
    const blurTimer = useRef(null);
    const show = () => { clearTimeout(blurTimer.current); setOpen(true); };

    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) { setResults([]); setLoading(false); return undefined; }
        const id = ++reqId.current;
        setLoading(true);
        const timer = setTimeout(() => {
            api.get('/reports/users/search', { params: { q } })
                .then(res => { if (id === reqId.current) setResults(res.data.users || []); })
                .catch(() => { if (id === reqId.current) setResults([]); })
                .finally(() => { if (id === reqId.current) setLoading(false); });
        }, 300);
        return () => clearTimeout(timer);
    }, [api, query]);

    const pick = (u) => { onSelect(u); setQuery(''); setOpen(false); };

    return (
        <div className="relative w-full sm:w-80" data-testid="report-user-search">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
                ref={inputRef}
                value={query}
                onChange={(e) => { setQuery(e.target.value); show(); }}
                onFocus={show}
                onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && results[0]) pick(results[0]); if (e.key === 'Escape') setOpen(false); }}
                placeholder="Search users by name or email..."
                className="pl-9 pr-9"
                data-testid="report-user-search-input"
            />
            {loading ? <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                : query && <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { setQuery(''); inputRef.current?.focus(); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="report-user-search-clear"><X className="h-4 w-4" /></button>}
            {open && query.trim().length >= 2 && !loading && (
                <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border bg-popover shadow-lg" data-testid="report-user-search-results">
                    {results.length === 0 ? (
                        <p className="px-3 py-3 text-sm text-muted-foreground" data-testid="report-user-search-empty">No users match &quot;{query.trim()}&quot;</p>
                    ) : results.map(u => (
                        <button key={u.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(u)}
                            className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-muted"
                            data-testid={`report-user-option-${u.id}`}>
                            <UserRound className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span className="min-w-0">
                                <span className="block truncate text-sm font-medium">{u.name || u.email}</span>
                                <span className="block truncate text-xs text-muted-foreground">{u.email}{u.department ? ` · ${u.department}` : ''}</span>
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
